import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useState, type ReactNode, type RefObject} from "react";
import {ArchiveConfirmationModal} from "~/components/archiveConfirmationModal";
import {CancelCallModal} from "~/components/dhiraaguCancelCallAction";
import {getArchiveDisabledReasonFromRules} from "~/components/eventArchiveAction";
import {invalidateDataViewerQueriesAfterEventArchive} from "~/components/ottopilotDataViewer/invalidateOttopilotDataViewerQueries";
import {dismissDataViewerRowEngagementPanel} from "~/components/ottopilotDataViewer/rowEngagement/dismissDataViewerRowEngagementPanel";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {OttopilotEventStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEvent";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {isAmaronWorkspace} from "~/domainModels/workspaceSpecific/amaron";
import {amaronArchiveEventAction} from "~/utilities/apis/amaronActionsApi";
import {isDuluxWorkspace} from "~/domainModels/workspaceSpecific/dulux";
import {duluxArchiveEventAction} from "~/utilities/apis/duluxActionsApi";

import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {updateEvent, type EventWithEventContainer} from "~/utilities/apis/api";

export function useEventArchiveEngagement({
    queryKeyPrefix,
    archiveMenuItemLabel = "Archive",
    controllerRef,
    pageEventTemplate,
    /**
     * When true, skip built-in {@link ArchiveConfirmationModal} so the host can render
     * config-driven confirm chrome (`cta.action.presentation` + `cta.action.confirm`).
     */
    suppressConfirmationUi = false,
    afterInvalidate,
}: {
    queryKeyPrefix: Array<unknown>;
    archiveMenuItemLabel?: string;
    controllerRef?: RefObject<OttopilotDataViewerController<EventWithEventContainer> | null>;
    /** Current events page template (e.g. Amaron archive config merged on the client). */
    pageEventTemplate?: OttopilotEventTemplate;
    suppressConfirmationUi?: boolean;
    /** Extra cache refresh after the default event-archive invalidation (config-driven surfaces). */
    afterInvalidate?: () => Promise<void>;
}) {
    const [archiveTarget, setArchiveTarget] = useState<EventWithEventContainer | null>(null);
    const [cancelCallModal, setCancelCallModal] = useState<{
        event: EventWithEventContainer;
        queryKeyPrefix: Array<unknown>;
    } | null>(null);
    const {heimdallUser} = useWorkspaceUser();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {workspace, workspaceCapabilities} = useWorkspace();
    const queryClient = useQueryClient();
    const {showFlag} = useAnankeFlags();
    const eventTemplates = useEventTemplates();

    const resolveEventTemplate = (templateId: Uuid): OttopilotEventTemplate | undefined => {
        if (pageEventTemplate !== undefined && pageEventTemplate.id === templateId) {
            return pageEventTemplate;
        }
        return eventTemplates.find((template) => template.id === templateId);
    };

    const resolveStatusColumnConfig = (templateId: Uuid) => {
        return resolveEventTemplate(templateId)?.configuration.statusColumnConfig;
    };

    const usesWorkspaceSpecificArchive =
        isAmaronWorkspace(workspaceCapabilities) === true || isDuluxWorkspace(workspaceCapabilities) === true;
    const isArchiveAvailable =
        usesWorkspaceSpecificArchive === true
        || pageEventTemplate?.configuration.statusColumnConfig !== undefined
        || eventTemplates.some((template) => template.configuration.statusColumnConfig !== undefined);

    const getArchiveDisabledReason = (event: EventWithEventContainer): string | null => {
        if (usesWorkspaceSpecificArchive === true) {
            const template = resolveEventTemplate(event.templateId);
            return getArchiveDisabledReasonFromRules({
                rules: template?.configuration.archiveConfig?.rules ?? [],
                additionalColumnValues: event.additionalColumnValues,
            });
        }
        const columnConfig = resolveStatusColumnConfig(event.templateId);
        if (columnConfig === undefined) {
            return "Archive is not available";
        }
        const template = resolveEventTemplate(event.templateId);
        return getArchiveDisabledReasonFromRules({
            rules: template?.configuration.archiveConfig?.rules ?? [],
            additionalColumnValues: event.additionalColumnValues,
        });
    };

    const archiveMutation = useMutation({
        mutationFn: async () => {
            if (archiveTarget === null) {
                throw new Error("Status column configuration not found for this event type");
            }
            if (isAmaronWorkspace(workspaceCapabilities) === true) {
                const result = await amaronArchiveEventAction({
                    accessToken: heimdallUser.accessToken,
                    intellsysBaseUrl: intellsysBaseUrl,
                    request: {
                        workspaceId: workspace.id,
                        eventId: archiveTarget.id,
                        templateId: archiveTarget.templateId,
                    },
                });
                if (result.success !== true) {
                    throw result.error;
                }
                return;
            }

            if (isDuluxWorkspace(workspaceCapabilities) === true) {
                const result = await duluxArchiveEventAction({
                    accessToken: heimdallUser.accessToken,
                    intellsysBaseUrl: intellsysBaseUrl,
                    request: {
                        workspaceId: workspace.id,
                        eventId: archiveTarget.id,
                        templateId: archiveTarget.templateId,
                    },
                });
                if (result.success !== true) {
                    throw result.error;
                }
                return;
            }

            const statusColumnConfig = resolveStatusColumnConfig(archiveTarget.templateId);
            if (statusColumnConfig === undefined) {
                throw new Error("Status column configuration not found for this event type");
            }

            const result = await updateEvent({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl,
                eventId: archiveTarget.id,
                templateId: archiveTarget.templateId,
                workspaceId: workspace.id,
                data: {
                    status: OttopilotEventStatus.archived,
                    [statusColumnConfig.columnId]: statusColumnConfig.archiveStatusValue,
                } as never,
            });

            if (result.success !== true) {
                throw result.error;
            }
        },
        onSuccess: () => {
            showFlag({
                title: "Event archived",
                description: "The event was archived successfully.",
                appearance: "success",
                isAutoDismiss: true,
                autoDismissSeconds: 3,
            });
            setArchiveTarget(null);
            dismissDataViewerRowEngagementPanel(controllerRef);
            void (async () => {
                await invalidateDataViewerQueriesAfterEventArchive(
                    queryClient,
                    queryKeyPrefix,
                    workspace.id,
                );
                if (afterInvalidate !== undefined) {
                    await afterInvalidate();
                }
            })();
        },
        onError: (error: unknown) => {
            showFlag({
                title: "Archive failed",
                description:
                    error instanceof Error ? error.message : "Could not archive the event. Please try again.",
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 4,
            });
        },
    });

    const requestArchive = (event: EventWithEventContainer) => {
        const disabledReason = getArchiveDisabledReason(event);
        if (disabledReason !== null) {
            showFlag({
                title: "Cannot Archive",
                description: disabledReason,
                appearance: "warning",
                isAutoDismiss: true,
                autoDismissSeconds: 4,
            });
            return;
        }
        setArchiveTarget(event);
    };

    const archiveModals: ReactNode =
        suppressConfirmationUi === true
            ? cancelCallModal !== null
                ? (
                      <CancelCallModal
                          event={cancelCallModal.event}
                          queryKeyPrefix={cancelCallModal.queryKeyPrefix}
                          isOpen={true}
                          setIsOpen={(open) => {
                              if (open === false) {
                                  setCancelCallModal(null);
                              }
                          }}
                      />
                  )
                : null
            : archiveTarget !== null && isArchiveAvailable === true
              ? (
                    <>
                        <ArchiveConfirmationModal
                            isOpen={true}
                            setIsOpen={(open) => {
                                if (open === false) {
                                    setArchiveTarget(null);
                                }
                            }}
                            itemType="event"
                            onConfirm={async () => {
                                await archiveMutation.mutateAsync();
                            }}
                            isLoading={archiveMutation.isPending}
                        />
                        {cancelCallModal !== null ? (
                            <CancelCallModal
                                event={cancelCallModal.event}
                                queryKeyPrefix={cancelCallModal.queryKeyPrefix}
                                isOpen={true}
                                setIsOpen={(open) => {
                                    if (open === false) {
                                        setCancelCallModal(null);
                                    }
                                }}
                            />
                        ) : null}
                    </>
                )
              : null;

    const dismissArchive = () => {
        setArchiveTarget(null);
    };

    return {
        isArchiveAvailable,
        archiveMenuItemLabel,
        getArchiveDisabledReason,
        requestArchive,
        dismissArchive,
        archiveModals,
        setCancelCallModal,
        archiveTargetEventId: archiveTarget?.id ?? null,
        isArchiveConfirmPending: archiveTarget !== null,
        isArchiveMutationPending: archiveMutation.isPending,
        confirmArchive: async () => {
            await archiveMutation.mutateAsync();
        },
    };
}
